define((require) => {
    "use strict";

    var {isEmpty, debounce}                     = require("underscore"),
        Preconditions                           = require("shared/util/Preconditions"),
        Promise                                 = require("shared/util/Promise"),
        SizeAndPositionPropertiesView           = require("ui/experience/widgets/SizeAndPositionPropertiesView"),
        {isBlank}                               = require("shared/util/StringUtils"),
        SalesforceCommerce                      = require("ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceConnect"),
        SalesforceCommerceOCAPIServiceFactory   = require("ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceOCAPIServiceFactory"),
        SalesforceCommerceProductCollectionView = require("ui/experience/widgets/salesforceCommerceWidget/view/SalesforceCommerceProductCollectionView"),
        template                                = require("ui/experience/widgets/salesforceCommerceWidget/view/SalesforceCommerceWidgetPropertiesView.tpl"),
        AbstractWidgetPropertiesView            = require("ui/experience/widgets/AbstractWidgetPropertiesView");

    const EXPAND_PROPERTY = "demandwareExpanded";

    /**
     * The properties view for an SalesforceCommerce widget.
     *
     * @class SalesforceCommerceWidgetPropertiesView
     * @cfg {SalesforceCommerceWidgetModel} model The model for the SalesforceCommerce widget being edited.
     */
    return AbstractWidgetPropertiesView.extend("DemandwareWidgetPropertiesView", {

        expandProperty: EXPAND_PROPERTY,

        template: template,

        /**
         * A flag indicating if the user has interacted with the <tt>search params</tt>.
         */
        _userHasInteracted: false,

        templateHelpers: function() {
            var siteCollection = SalesforceCommerce.getSettings().get("sites");

            return {
                sites: siteCollection.models
            };
        },

        ui: {
            connect: ".connect",
            salesforceCommercePropertiesAccordion: ".salesforce-commerce-properties-accordion",
            salesforceCommerceItem: ".salesforce-commerce-item",
            legend: "legend",
            sites: ".sites",
            search: ".search-products",
            searchInput: ".search",
            connectionError: ".connection-error"
        },

        regions: {
            accountViewRegion:                      ".account-view-region",
            salesforceCommerceResults:              ".salesforce-commerce-results",
            sizeAndPositionProperties:              ".size-and-position-properties",
            salesforceCommerceSettingsProperties:   ".salesforce-commerce-settings-properties"
        },

        events: {
            "keyup @ui.search":                     "_searchParamChangeHandler",
            "change @ui.sites":                     "_siteChangeHandler",
            "select @ui.salesforceCommerceItem":    "_itemSelectedHandler"
        },

        /*
         * When an item is selected, we set that item to the widget and highlight that item view
         */
        _itemSelectedHandler: function(event, model) {
            this._updateModel(model.get("productID"), this.ui.sites.val());
            this.salesforceCommerceProductCollectionView.highlightSelectedProduct(this.model.get("productID"));
        },

        initialize: function() {
            this.__super();
            this.salesforceCommerceSetting = SalesforceCommerce.getSettings();
        },

        _siteChangeHandler: function() {
            this._verifyConnection();
            this._searchParamChangeHandler();

            // Always make sure that the site Id is set on the model when the site Id changes
            this._updateModel(null, this.ui.sites.val());
        },

        /**
         * @private
         * Updates the model with the product ID and site ID if they are set.
         * The model won't be updated if the passed values are undefined.
         *
         * @param productID the ID of the product.
         * @param siteID the ID of the site
         */
        _updateModel: function(productID, siteID) {
            var values = {};

            if (productID) {
                values.productID = productID;
            }

            if (siteID) {
                values.siteId = siteID;
            }

            if (!isEmpty(values)) {
                this.model.set(values);
            }
        },

        _verifyConnection: function() {
            var self = this,
                site = null,
                siteId = this.ui.sites.val(),
                // SalesforceCommerce settings
                settings = this.salesforceCommerceSetting;

            if (siteId) {
                site =  settings.get("sites").get(siteId).get("siteId");
            }

            self.ui.connectionError.html("");
            // Test the connection to see if we can query product from the selected site, shows the error if it fails
            SalesforceCommerce.inferAddress(settings.get("userUrl"), settings.get("clientId"), site)
                .fail(() => {
                    self.salesforceCommerceResults.reset();
                    self.ui.connectionError.html("We cannot connect to the site <b>" + site + "</b>. No search results will be shown.");
                });
        },

        /**
         * Handler the user inputs and change of site selection. We save the last query for usability.
         * This function is debounced to avoid too many calls
         */
        _searchParamChangeHandler: debounce(function() {
            var query = this.ui.searchInput.val(),
                siteId = this.ui.sites.val(),
                propertyState = this.viewScope.viewModel.get("widgetPropertyViewsState");

            if (query !== propertyState.lastQuery || siteId !== propertyState.lastSiteId) {
                // In fact, the user has interacted with some different input
                this._userHasInteracted = true;
            }

            if (isBlank(query)) {
                this.salesforceCommerceResults.close();
            } else {
                this._getProducts();
            }

            propertyState.lastQuery = query;
            propertyState.lastSiteId = siteId;
        }, 400),

        /**
         * If the user has changed something, then try to get products, either query from server or use the cache.
         *
         * @private
         */
        _getProducts: function() {
            var siteCollection = this.salesforceCommerceSetting.get("sites"),
                query = this.ui.searchInput.val(),
                siteId = this.ui.sites.val(),
                site = null;

            if (siteId) {
                site =  siteCollection.get(siteId).get("siteId");
            }

            this._search(query, site).done(this._showProductCollection.bind(this));
        },

        /**
         * Search products using user selected query and sites from the <i>SalesforceCommerce Open Commerce Api</i>.
         *
         * @param {String} query    The search query.
         * @param {String} site     The site name to be searched from
         * @private
         */
        _search: function(query, site) {
            var self = this,
                searchPromise = SalesforceCommerceOCAPIServiceFactory.getService().search(query, site);

            return Promise.predicate(searchPromise, this._viewActive)
                .done((productCollection) => {
                    // If the user hasn't interacted and the productCollection has no results, It means that
                    // the OCAPI server wasn't able to find the productId. In this case, we will log an exception
                    // to check how often it happens.
                    Preconditions.checkState(
                        self._userHasInteracted || !!productCollection,
                        "The user has selected a Product ('" + query + "'), but the OCAPI didn't bring any results."
                    );
                });
        },

        /**
         * Shows the <tt>SalesforceCommerceProductCollection</tt> to the view.
         *
         * @param {SalesforceCommerceProductCollection} productCollection   The collection to be shown
         */
        _showProductCollection: function(productCollection) {
            if (this.isClosed) {
                return;
            }

            this.salesforceCommerceProductCollectionView = new SalesforceCommerceProductCollectionView({
                collection: productCollection,
                itemViewOptions: {
                    showTooltips: true,
                    showImageInfo: true,
                    dropTargetSize: "large",
                    draggable: true
                }
            });

            this.salesforceCommerceResults.show(this.salesforceCommerceProductCollectionView);
            this.salesforceCommerceProductCollectionView.highlightSelectedProduct(this.model.get("productID"));
        },

        /**
         * Validate if the user has already entered some settings. This method doesn't
         * performs a server side validations, however, check if the clientId and the userUrl are valid.
         *
         * @private
         */
        _validateServerSettings: function() {
            var self = this,
                isSettingsOk = SalesforceCommerce.validateServerSettings(this.salesforceCommerceSetting);

            self.ui.connect.toggle(!isSettingsOk);
            self.ui.search.toggle(isSettingsOk);
        },

        onRender: function() {
            this.__super();
            // Let's check if the user, at least, has some valid settings in the client side.
            this._validateServerSettings();

            var propertyState = this.viewScope.viewModel.get("widgetPropertyViewsState"),
                searchValue = this.model.get("productID") || propertyState.lastQuery,
                searchSite = this.model.get("siteId") || propertyState.lastSiteId;

            if (searchSite) {
                this.ui.sites.val(searchSite);
            }

            if (searchValue) {
                this.ui.searchInput.val(searchValue);
                this._getProducts();
            }

            if (!this.salesforceCommerceSetting.get("userUrl") || !this.salesforceCommerceSetting.get("clientId")) {
                this.ui.connect.toggle(true);
                this.ui.search.toggle(false);
            }

            this._verifyConnection();
        },

        onShow: function() {
            this.__super();
            this.sizeAndPositionProperties.show(new SizeAndPositionPropertiesView({model: this.model, widgetView: this.options.widgetView}));
        },

        _shouldExpandOnRender() {
            return this.viewScope.viewModel.get("widgetPropertyViewsState")[this.expandProperty] || this.model.has("productID");
        },

        _viewActive: function() {
            return !this.isClosed;
        }
    }, {
        EXPAND_PROPERTY
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/view/SalesforceCommerceWidgetPropertiesView.js